import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { CheckCircle, XCircle, Loader2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTerritory } from "@/contexts/TerritoryContext";

const STORAGE_KEY = "edufocal_pending_trial";

export default function TrialLink() {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const { territory } = useTerritory();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [offer, setOffer] = useState<{ type: string; value: number; deadline: string | null } | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!slug) {
      setStatus("error");
      setErrorMessage("Invalid trial link.");
      return;
    }

    // An offer link emailed to one person carries a signed ?o= token with its own deadline.
    // It has to travel with the code all the way to checkout, so it is saved alongside it.
    const offerToken = new URLSearchParams(window.location.search).get("o");
    fetch(`/api/trial/${encodeURIComponent(slug)}${offerToken ? `?o=${encodeURIComponent(offerToken)}` : ""}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || "This trial link is not valid.");
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          code: data.code,
          type: data.type,
          value: data.value,
          applicablePlans: data.applicablePlans,
          offerToken: data.offerToken ?? offerToken ?? null,
          offerExpiresAt: data.offerExpiresAt ?? null,
          offerDeadline: data.offerDeadline ?? null,
          slug,
          savedAt: Date.now(),
        }));
        setOffer({ type: data.type, value: data.value, deadline: data.offerDeadline ?? null });
        setStatus("success");

        const timer = setTimeout(() => navigate("/signup"), 3000);
        return () => clearTimeout(timer);
      })
      .catch((err) => {
        setStatus("error");
        setErrorMessage(err.message || "This trial link is not valid.");
      });
  }, [slug, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-chart-2/5 px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="p-4 bg-primary/10 rounded-2xl">
            <GraduationCap className="w-10 h-10 text-primary" />
          </div>
        </div>

        <div>
          <h1 className="text-2xl font-bold mb-1">EduFocal Academy</h1>
          <p className="text-muted-foreground text-sm">{territory.name}'s #1 {territory.examName} Prep Platform</p>
        </div>

        {status === "loading" && (
          <div className="space-y-3">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">Applying your offer...</p>
          </div>
        )}

        {status === "success" && offer && (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-6 space-y-3">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400 mx-auto" />
              <h2 className="text-xl font-bold text-green-800 dark:text-green-200">
                {offer.type === "trial" ? `${offer.value}-Day Free Trial Unlocked` : `${offer.value}% Off Your First Month`}
              </h2>
              <p className="text-green-700 dark:text-green-300 text-sm">
                {offer.type === "trial"
                  ? "Your free trial is applied. Create your account to start, and cancel any time inside the trial."
                  : "Your discount is applied. Create your account and it will be waiting at checkout."}
              </p>
              {offer.deadline && (
                <p className="text-green-700 dark:text-green-300 text-xs font-semibold">
                  This offer closes {offer.deadline}.
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Redirecting you to sign up in a moment...</p>
            <Button className="w-full" onClick={() => navigate("/signup")}>
              Create Your Free Account
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-6 space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h2 className="text-xl font-bold text-red-800 dark:text-red-200">Link Not Available</h2>
              <p className="text-red-700 dark:text-red-300 text-sm">{errorMessage}</p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => navigate("/")}>
              Go to Homepage
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
